import React, { useState, useEffect } from 'react';
import './styles/App.css';
import EventTable from './components/EventTable';
import AboutSection from './components/AboutSection';
import FilterSection from './components/FilterSection';
import useFetchEvents from './hooks/useFetchEvents';
import useNotification from './hooks/useNotification'; // Import the hook
import LoadingBar from './components/LoadingBar';
import { getEmptyFilters } from './utils/filterReset'; // Import getEmptyFilters
import Attributions from './components/Attributions'; // Import the new Attributions component

function App() {
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [filters, setFilters] = useState({
    group: [],
    availability: [],
    location: [],
    dayOfWeek: [],
    dates: [],
    difficulty: [],
    venueSearch: [], // Add this line
    groupOpen: false,
    availabilityOpen: false,
    locationOpen: false,
    dayOfWeekOpen: false,
  });
  const [searchInitiated, setSearchInitiated] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const { notification, showNotification } = useNotification(); // Use the hook
  const [eventsCollapsed, setEventsCollapsed] = useState(false);

  const { fetchData, loading, loadingProgress } = useFetchEvents(setRows, setFilteredRows, setSearchInitiated);

  useEffect(() => {
    let mounted = true;
    const loadData = async () => {
      if (mounted) {
        await fetchData();
      }
    };
    loadData();
    return () => {
      mounted = false;
    };
  }, [fetchData]); // Add fetchData as a dependency since it's memoized in useFetchEvents

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 overflow-x-hidden">
      {/* Loading overlay */}
      {loading && <LoadingBar progress={loadingProgress} />}
      <div className="container mx-auto py-4 max-w-screen-lg"> {/* Added max-w-screen-lg */}
        <h1 className="text-center mb-4"> {/* Reduced bottom margin from mb-8 to mb-6 */}
          <img
            src="/FindPickleball-Large-Logo.png"
            alt="Find Pickleball Logo"
            className="mx-auto w-48 sm:w-64 md:w-72 lg:w-80"
          />
        </h1>
        <div className="space-y-4"> {/* Reduced spacing */}
          <AboutSection
            isAboutOpen={isAboutOpen}
            toggleAboutSection={() => setIsAboutOpen(!isAboutOpen)}
          />
          {/* Tabbed Interface */}
          <div className="flex border-b border-gray-300">
            <button
              className={`flex-1 px-4 py-2 text-center font-semibold ${
                !eventsCollapsed
                  ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white border-t border-l border-r rounded-t-lg'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              onClick={() => {
                setEventsCollapsed(false);
              }}
            >
              Events ({filteredRows.length})
            </button>
            <button
              className={`flex-1 px-4 py-2 text-center font-semibold relative ${
                eventsCollapsed
                  ? 'bg-gradient-to-r from-teal-600 to-cyan-600 text-white border-t border-l border-r rounded-t-lg'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              onClick={() => {
                setEventsCollapsed(true);
              }}
            >
              Filters
              <div
                onClick={() => setFilters(getEmptyFilters())}
                className={`absolute right-2 top-1/2 transform -translate-y-1/2 w-6 h-6 rounded-full text-white text-xs flex items-center justify-center cursor-pointer ${
                  Object.values(filters).some(filter => Array.isArray(filter) && filter.length > 0)
                    ? 'bg-red-500 hover:bg-red-600'
                    : 'bg-gray-300 cursor-not-allowed'
                }`}
                title="Reset filter selections"
                role="button"
                aria-disabled={!Object.values(filters).some(filter => Array.isArray(filter) && filter.length > 0)}
              >
                <span
                  style={{
                    display: 'inline-block',
                    transform: 'scaleX(-1) rotate(90deg)', // Flip horizontally and rotate 90 degrees
                  }}
                >
                  &#x21bb;
                </span>
              </div>
            </button>
          </div>
          {/* Conditional Rendering Based on Active Tab */}
          <div className="bg-white pt-0 pb-2 px-1 rounded-b-lg shadow-md">
            {!eventsCollapsed && (
              <EventTable
                rows={filteredRows}
                searchInitiated={searchInitiated}
                fetchData={fetchData}
                showNotification={showNotification}
              />
            )}
            {eventsCollapsed && (
              <FilterSection
                rows={rows}
                filters={filters}
                setFilters={setFilters}
                setFilteredRows={setFilteredRows}
              />
            )}
          </div>
        </div>
      </div>
      {/* Notification Display */}
      {notification && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}
      {/* Attributions Component */}
      <Attributions />
    </div>
  );
}

export default App;
